import { Icon, Tooltip } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import styled from "styled-components";

import {
  EBooleanSearchTypes,
  ELieuFormationAgrementStatut,
  OffreDemandeGridDto,
  LieuFormationAgrementSearchDto,
  OffreDemandeApi
} from "../../../../api";
import { ERoutes } from "../../../../AppRouter";
import {
  BooleanColumn,
  CircleColumn,
  CustomBulletList,
  EditButton,
  LinkButton,
  SearchTablePage,
  ViewButton
} from "../../../../components";
import { useAbortableApiServiceFactory, useApiService, useTheme, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

const LegendHeaderContainer = styled.div`
  & > * + * {
    margin-left: 0.5rem;
  }
`;

export interface IOffreListPageProps {}

export const OffreListPage: React.FunctionComponent<IOffreListPageProps> = () => {
  const { t, tUnsafe } = useTl();
  const { theme } = useTheme();
  const { push } = useHistory();
  const api = useApiService(OffreDemandeApi);

  const apiFactory = useAbortableApiServiceFactory(OffreDemandeApi);
  const lastAbortController = React.useRef<AbortController>();
  const searchFunction = React.useCallback(
    (sObj?: LieuFormationAgrementSearchDto) => {
      sObj.excludedStatuts = [ELieuFormationAgrementStatut.Suspendu, ELieuFormationAgrementStatut.Retrait];
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.offreDemandeBaseSearch({ OffreDemandeSearchDto: sObj });
    },
    [apiFactory]
  );

  const computeType = React.useCallback(
    (row: OffreDemandeGridDto) => {
      if (row.agrementPartielA && row.agrementPartielB) return t(ETLCodes.PartielAB);
      if (row.agrementPartielA) return t(ETLCodes.PartielA);
      if (row.agrementPartielB) return t(ETLCodes.PartielB);
      return t(ETLCodes.Complet);
    },
    [t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: OffreDemandeGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => push(`${ERoutes.offre}/${row.idlieuFormationAgrementOffre}/detail/view`)}
            />
            <EditButton
              minimal={true}
              onClick={() => push(`${ERoutes.offre}/${row.idlieuFormationAgrementOffre}/detail/edit`)}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.LieuFormation),
        fieldName: "nomLieuFormation",
        render: (row: OffreDemandeGridDto) => (
          <LinkButton
            text={row.nomLieuFormation}
            onClick={() =>
              window.open(
                `#${ERoutes.lieuFormation}/${row.idlieuFormation}/detail/view?idsiegeSocial=${row.idsiegeSocial}`
              )
            }
          />
        )
      },
      {
        header: () => t(ETLCodes.Enseigne),
        fieldName: "enseigneLieuFormation"
      },
      {
        header: () => t(ETLCodes.Active),
        fieldName: "offreActive",
        autoFitContent: true,
        alignment: "center",
        render: (row: OffreDemandeGridDto) => <BooleanColumn value={row.offreActive} />
      },
      {
        header: () => (
          <LegendHeaderContainer>
            <span>{t(ETLCodes.C)}</span>
            <Tooltip
              content={
                <CustomBulletList
                  title={t(ETLCodes.Legende)}
                  items={[
                    {
                      text: t(ETLCodes.CapaciteNonAtteinte),
                      color: theme.sucessColor
                    },
                    {
                      text: t(ETLCodes.CapaciteAtteinte),
                      color: theme.dangerColor
                    }
                  ]}
                />
              }
              popoverClassName="bullet-list-popover"
              position="right"
            >
              <Icon icon="info-sign" style={{ cursor: "pointer" }} />
            </Tooltip>
          </LegendHeaderContainer>
        ),
        fieldName: "capaciteAtteinte",
        autoFitContent: true,
        render: (row: OffreDemandeGridDto) => (
          <CircleColumn color={row.capaciteAtteinte ? theme.dangerColor : theme.sucessColor} />
        )
      },
      {
        header: () => t(ETLCodes.CodeMetier),
        fieldName: "codeMetier"
      },
      {
        header: () => t(ETLCodes.NomMetier),
        fieldName: "nomMetier"
      },
      {
        header: () => t(ETLCodes.Stade),
        fieldName: "nomStade"
      },
      {
        header: () => t(ETLCodes.Statut),
        fieldName: "statut",
        render: row => tUnsafe(`StatutAgrement_${row.statut}`)
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "type",
        render: (row: OffreDemandeGridDto) => computeType(row)
      },
      {
        header: () => t(ETLCodes.DateDecision),
        fieldName: "dateDecision"
      }
    ],
    [computeType, push, t, tUnsafe, theme.dangerColor, theme.sucessColor]
  );

  const getCriteria = React.useCallback(() => api.offreDemandeGetSearchCriterias({ includeListsValues: true }), [api]);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <SearchTablePage
      getCriteriasFunction={getCriteria}
      searchStateInitialSearch={false}
      searchFunction={searchFunction}
      onAbort={onAbort}
      columns={columns}
      defaultCriterias={[
        {
          criteria: "OffreActive",
          searchMode: EBooleanSearchTypes.Equals,
          value: true
        }
      ]}
      breadCrumbs={[{ text: t(ETLCodes.Offres) }]}
      sortKeys={{ codeMetier: "ASC" }}
      keyFieldName="idlieuFormationAgrement"
    />
  );
};
